import type { ConsoleProviderConfig } from "../../schema/provider-core.js";
import type { TelemetryEvent, TelemetryLevel, TelemetryProvider } from "../../core/types.js";
import { isErrorEventKind, isLogEventKind } from "../../core/types.js";

function resolveConsoleMethod(
  configured: ConsoleProviderConfig["level"],
  eventLevel: TelemetryLevel | undefined,
  isError: boolean,
): "debug" | "info" | "warn" | "error" {
  if (isError) {
    if (eventLevel === "warn") return "warn";
    return configured === "debug" || configured === "info" ? "error" : (configured ?? "error");
  }

  switch (eventLevel) {
    case "trace":
    case "debug":
      return "debug";
    case "warn":
      return "warn";
    case "error":
    case "fatal":
      return "error";
    default:
      return configured === "error" ? "info" : (configured ?? "info");
  }
}

export function createConsoleProvider(
  config: ConsoleProviderConfig,
): TelemetryProvider {
  const id = config.id ?? "console";
  const configuredLevel = config.level ?? "error";

  return {
    id,
    type: "console",
    send(event: TelemetryEvent) {
      const payload = JSON.stringify(event, null, 2);
      const isError = isErrorEventKind(event.kind);
      const method = resolveConsoleMethod(configuredLevel, event.level, isError);

      if (isLogEventKind(event.kind)) {
        const prefix = `[telemetry:${id}] ${event.kind}`;
        console[method](prefix, event.message, event.attributes ?? {});
        return;
      }

      switch (method) {
        case "debug":
          console.debug(`[telemetry:${id}]`, payload);
          break;
        case "info":
          console.info(`[telemetry:${id}]`, payload);
          break;
        case "warn":
          console.warn(`[telemetry:${id}]`, event.error?.message ?? event.message, event);
          break;
        default:
          console.error(`[telemetry:${id}]`, event.error?.message ?? event.message, event);
      }
    },
  };
}
